import { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useTerritory } from "@/contexts/TerritoryContext";
import { useAuth } from "@/contexts/AuthContext";
import { TERRITORIES, type TerritoryCode } from "@shared/territories";
import logoUrl from "@assets/edufocal-logo-min-50292a59_1758905778671.png";

function TerritorySelector() {
  const { territoryCode, setTerritory } = useTerritory();

  return (
    <select
      value={territoryCode}
      onChange={(e) => setTerritory(e.target.value as TerritoryCode)}
      className="text-sm border border-border rounded-lg px-2 py-1.5 bg-card text-muted-foreground font-semibold cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30"
      aria-label="Select country"
      data-testid="select-territory"
    >
      {Object.values(TERRITORIES).map(t => (
        <option key={t.code} value={t.code}>{t.flag} {t.name}</option>
      ))}
    </select>
  );
}

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);
  const { isAuthenticated } = useAuth();
  const { showSelector } = useTerritory();

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY <= 0) {
        setVisible(true);
      } else if (currentY > lastScrollY.current) {
        setVisible(false); // scrolling down
      } else {
        setVisible(true);  // scrolling up
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Authenticated users get the in-app TopNavigation rendered globally, so we
  // hide this marketing-site header for them. Done AFTER all hooks so we
  // never change the hook count between renders (avoids the
  // "Rendered fewer hooks than expected" crash when auth state flips).
  if (isAuthenticated) return null;

  return (
    <header className={`sticky top-0 z-50 w-full bg-background/95 backdrop-blur-sm border-b border-border transition-transform duration-300 ${visible ? 'translate-y-0' : '-translate-y-full'}`}>
      <div className="container flex h-16 items-center justify-between px-4 md:px-6 max-w-7xl mx-auto">
        {/* Logo */}
        <div className="flex items-center">
          <a href="/" className="flex items-center space-x-2" data-testid="link-home">
            <div className="relative">
              <img src={logoUrl} alt="EduFocal Academy Logo" className="h-10 w-auto" data-testid="img-logo" />
            </div>
          </a>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-2">
          <nav className="flex items-center space-x-1 mr-4">
            <Button
              variant="ghost"
              className="text-muted-foreground hover:text-primary hover:bg-primary/5 font-semibold text-sm min-h-9 px-4 rounded-lg transition-colors"
              data-testid="button-features"
              onClick={() => window.location.href = '/features'}
            >
              Features
            </Button>
            <Button
              variant="ghost"
              className="text-muted-foreground hover:text-primary hover:bg-primary/5 font-semibold text-sm min-h-9 px-4 rounded-lg transition-colors"
              data-testid="button-how-it-works"
              onClick={() => window.location.href = '/how-it-works'}
            >
              How It Works
            </Button>
            <Button
              variant="ghost"
              className="text-muted-foreground hover:text-primary hover:bg-primary/5 font-semibold text-sm min-h-9 px-4 rounded-lg transition-colors"
              onClick={() => window.location.href = '/practice-papers'}
            >
              Practice Papers
            </Button>
            <Button
              variant="ghost"
              className="text-muted-foreground hover:text-primary hover:bg-primary/5 font-semibold text-sm min-h-9 px-4 rounded-lg transition-colors"
              data-testid="button-blog"
              onClick={() => window.location.href = '/blog'}
            >
              Blog
            </Button>
            <Button
              variant="ghost"
              className="text-muted-foreground hover:text-primary hover:bg-primary/5 font-semibold text-sm min-h-9 px-4 rounded-lg transition-colors"
              data-testid="button-get-in-touch"
              onClick={() => window.location.href = '/contact'}
            >
              Get In Touch
            </Button>
          </nav>
          <div className="flex items-center space-x-3">
            {showSelector && <TerritorySelector />}
            <Button
              variant="ghost"
              className="text-muted-foreground hover:text-primary font-semibold text-sm min-h-9 px-4 rounded-lg"
              data-testid="button-sign-in"
              onClick={() => window.location.href = '/login'}
            >
              Sign In
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-sm min-h-10 px-5 rounded-lg shadow-sm transition-colors"
              data-testid="button-get-started"
              onClick={() => window.location.href = '/signup'}
            >
              Get Started
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="flex md:hidden items-center space-x-2">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="min-h-12 min-w-12"
                data-testid="button-mobile-menu"
              >
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background">
              <div className="flex flex-col space-y-4 mt-6">
                <div className="flex items-center justify-between pb-4 border-b border-border">
                  <div className="relative">
                    <img src={logoUrl} alt="EduFocal Academy Logo" className="h-10 w-auto" />
                  </div>
                </div>

                <nav className="flex flex-col space-y-1">
                  <Button
                    variant="ghost"
                    className="justify-start text-muted-foreground hover:text-primary hover:bg-primary/5 font-semibold min-h-12 rounded-lg"
                    data-testid="button-features-mobile"
                    onClick={() => {
                      window.location.href = '/features';
                      setIsOpen(false);
                    }}
                  >
                    Features
                  </Button>
                  <Button
                    variant="ghost"
                    className="justify-start text-muted-foreground hover:text-primary hover:bg-primary/5 font-semibold min-h-12 rounded-lg"
                    data-testid="button-how-it-works-mobile"
                    onClick={() => {
                      window.location.href = '/how-it-works';
                      setIsOpen(false);
                    }}
                  >
                    How It Works
                  </Button>
                  <Button
                    variant="ghost"
                    className="justify-start text-muted-foreground hover:text-primary hover:bg-primary/5 font-semibold min-h-12 rounded-lg"
                    onClick={() => {
                      window.location.href = '/practice-papers';
                      setIsOpen(false);
                    }}
                  >
                    Practice Papers
                  </Button>
                  <Button
                    variant="ghost"
                    className="justify-start text-muted-foreground hover:text-primary hover:bg-primary/5 font-semibold min-h-12 rounded-lg"
                    data-testid="button-blog-mobile"
                    onClick={() => {
                      window.location.href = '/blog';
                      setIsOpen(false);
                    }}
                  >
                    Blog
                  </Button>
                  <Button
                    variant="ghost"
                    className="justify-start text-muted-foreground hover:text-primary hover:bg-primary/5 font-semibold min-h-12 rounded-lg"
                    data-testid="button-get-in-touch-mobile"
                    onClick={() => {
                      window.location.href = '/contact';
                      setIsOpen(false);
                    }}
                  >
                    Get In Touch
                  </Button>
                </nav>

                <div className="flex flex-col space-y-3 pt-4 border-t border-border">
                  {showSelector && (
                    <div className="px-2">
                      <TerritorySelector />
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    className="justify-start text-muted-foreground hover:text-primary font-semibold min-h-12 rounded-lg"
                    data-testid="button-sign-in-mobile"
                    onClick={() => {
                      window.location.href = '/login';
                      setIsOpen(false);
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold min-h-12 rounded-lg"
                    data-testid="button-get-started-mobile"
                    onClick={() => {
                      window.location.href = '/signup';
                      setIsOpen(false);
                    }}
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}